import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Link } from 'react-router-dom';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
    document.body.classList.toggle('overflow-hidden', !menuOpen);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setMenuOpen(false);
    }
  };

  const toggleLanguage = () => {
    const newLanguage = i18n.language === 'mk' ? 'en' : 'mk';
    i18n.changeLanguage(newLanguage);
    localStorage.setItem('language', newLanguage); 
  };

  const handleMenuItemClick = () => {
    setMenuOpen(false); 
    document.body.classList.remove('overflow-hidden'); 
  };

  const handleSmoothScroll = (event, targetId) => {
    event.preventDefault(); 
    const targetElement = document.querySelector(targetId);
    if (targetElement) {
      const offsetTop = targetElement.getBoundingClientRect().top + window.scrollY - 120; 
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  };

  const handleNavigation = (event, targetId) => {
    event.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        handleSmoothScroll(event, targetId);
      }, 100); 
    } else {
      handleSmoothScroll(event, targetId);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage); 
    }
  }, [i18n]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const isHomePage = location.pathname === '/';

  return (
    <header className={`fixed top-0 w-full z-50 transition-colors duration-300 ${isHomePage && !isScrolled ? 'bg-transparent' : 'bg-white shadow-custom'}`}>
      <nav className="px-5 max-w-[1290px] w-full flex justify-between items-center mx-auto h-[80px]">
        <div className="flex gap-[48px] justify-center items-center">
          <Link to="/" className="flex items-center">
            <img
              src="/images/Logo.jpg"
              alt="Logo"
              className="w-[48px] h-[48px] object-contain"
            />
          </Link>

          <ul className={`hidden lg:flex gap-[32px] text-[16px] font-normal ${isHomePage && !isScrolled ? 'text-white' : 'text-[#3D3D3D]'}`}>
            <li className="relative group">
              <a
                href="/"
                className="relative"
                onClick={(event) => handleNavigation(event, '#about-us')}
              >
                {t('nav.aboutUs')}
                <span className="absolute top-[20px] left-0 w-full h-[2px] bg-[#F67E20] scale-x-0 group-hover:scale-x-100 transition-all duration-300 ease-in-out"></span>
              </a>
            </li>
            <li className="relative group">
              <a
                href="/"
                className="relative"
                onClick={(event) => handleNavigation(event, '#services')}
              >
                {t('nav.services')}
                <span className="absolute top-[20px] left-0 w-full h-[2px] bg-[#F67E20] scale-x-0 group-hover:scale-x-100 transition-all duration-300 ease-in-out"></span>
              </a>
            </li>
            <li className="relative group">
              <a
                href="/"
                className="relative"
                onClick={(event) => handleNavigation(event, '#team')}
              >
                {t('nav.ourTeam')}
                <span className="absolute top-[20px] left-0 w-full h-[2px] bg-[#F67E20] scale-x-0 group-hover:scale-x-100 transition-all duration-300 ease-in-out"></span>
              </a>
            </li>
            <li className="relative group">
              <Link to="/news" className="relative">
                {t('nav.news')}
                <span className="absolute top-[20px] left-0 w-full h-[2px] bg-[#F67E20] scale-x-0 group-hover:scale-x-100 transition-all duration-300 ease-in-out"></span>
              </Link>
            </li>
          </ul>
        </div>

        <div className="flex items-center space-x-4">
          <Link
            to="/contactus"
            className="lg:flex hidden items-center justify-center gap-2 p-[10px_24px] rounded-[12px] bg-[#FEEED6] text-[#F67E20] hover:bg-[#F67E20] hover:text-[#FEEED6] font-medium transition-all duration-300 ease-in-out"
          >
            {t('nav.contact')} <i className="fas fa-arrow-right ml-2"></i>
          </Link>
          <button
            onClick={toggleLanguage}
            className="lg:flex items-center justify-center p-[10px_16px] rounded-[12px] bg-[#FEEED6] text-[#F67E20] font-medium hover:bg-[#F67E20] hover:text-[#FEEED6] transition-all duration-300 ease-in-out"
          >
            {i18n.language === 'mk' ? 'MK' : 'EN'}
          </button>
          <button onClick={toggleMenu} className="lg:hidden">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#F67E20"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm z-50 flex flex-col items-center justify-center space-y-6 p-6">
          <button onClick={toggleMenu} className="absolute top-4 right-6 text-white">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <ul className="flex flex-col items-center gap-6 text-white text-[20px]">
            <li>
              <a
                href="#about-us"
                onClick={(event) => {
                  handleMenuItemClick();
                  handleNavigation(event, '#about-us');
                }}
              >
                {t('nav.aboutUs')}
              </a>
            </li>
            <li>
              <a
                href="#services"
                onClick={(event) => {
                  handleMenuItemClick();
                  handleNavigation(event, '#services');
                }}
              >
                {t('nav.services')}
              </a>
            </li>
            <li>
              <a
                href="#team"
                onClick={(event) => {
                  handleMenuItemClick();
                  handleNavigation(event, '#team');
                }}
              >
                {t('nav.ourTeam')}
              </a>
            </li>
            <li>
              <Link to="/news" onClick={handleMenuItemClick}>
                {t('nav.news')}
              </Link>
            </li>
          </ul>
          <Link
            to="/contactus"
            onClick={handleMenuItemClick}
            className="flex items-center justify-center gap-2 p-[10px_24px] rounded-[12px] bg-[#FEEED6] text-[#F67E20] hover:bg-[#F67E20] hover:text-[#FEEED6] font-medium transition-all duration-300 ease-in-out"
          >
            {t('nav.contact')} <i className="fas fa-arrow-right ml-2"></i>
          </Link>
        </div>
      )}
    </header>
  );
};

export default Navbar;
